<template>
  <a class="skip-link" href="#main-content">Skip to main content</a>
  <Ticker />
  <Navbar />
  <main ref="mainContent" id="main-content" tabindex="-1">
    <router-view/>
  </main>
  <Footer />
</template>


<script>
// @ is an alias to /src
import Footer from "@/components/Footer.vue";
import Navbar from "@/components/Navbar.vue";
import Ticker from "@/components/Ticker.vue";

export default {
  components: {
    Footer,
    Navbar,
    Ticker
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler(name) {
        const pageName = name || 'Page not found';
        document.title = pageName === 'Home'
          ? 'Elkhorn Valley Family Medicine'
          : `${pageName} | Elkhorn Valley Family Medicine`;
      }
    },
    '$route.fullPath': {
      async handler(path, previousPath) {
        if (path === previousPath) return;
        await this.$nextTick();
        let target = this.$refs.mainContent.querySelector('h1');
        if (this.$route.hash) {
          let id = this.$route.hash.slice(1);
          try {
            id = decodeURIComponent(id);
          } catch {
            // A malformed URL fragment must not interrupt keyboard navigation.
          }
          target = document.getElementById(id) || target;
        }
        if (target) {
          if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
      }
    }
  }
}
</script>

<style>
:root {
  --burgundy: #7f2532;
  --green: #234c35;
  --muted: #5c6660;
  --border: #e3dcd6;
  --surface: #fff;
}
*, *::before, *::after {
  box-sizing: border-box;
}
body {
  margin: 0;
  background: #faf7f3;
}
a {
  color: var(--burgundy);
  text-underline-offset: .2em;
}
a:focus-visible, button:focus-visible, summary:focus-visible, [tabindex]:focus-visible {
  outline: 3px solid var(--green);
  outline-offset: 4px;
}
h1[tabindex="-1"]:focus {
  outline: none;
}
main:focus-visible {
  outline-offset: -4px;
}
main {
  min-height: 45vh;
}
.page-shell {
  width: min(100% - 2.5rem, 72rem);
  margin: 0 auto;
  padding: 3rem 0 4rem;
  text-align: left;
  overflow-wrap: anywhere;
}
.reading-page {
  max-width: 48rem;
}
h1, h2, h3 {
  line-height: 1.25;
}
h1 {
  color: var(--burgundy);
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2.1rem, 4.5vw, 3.25rem);
  font-weight: 400;
  margin: 0 0 1.5rem;
}
h2, h3 {
  color: var(--burgundy);
}
.eyebrow {
  margin: 0 0 .75rem;
  color: var(--green);
  font-size: .8rem;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
}
.page-intro {
  max-width: 42rem;
  margin: 0 0 2rem;
  color: var(--muted);
  font-size: 1.1rem;
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: .75rem;
}
.action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .6rem;
  min-height: 48px;
  padding: .7rem 1.1rem;
  border: 1px solid var(--burgundy);
  border-radius: .35rem;
  font-weight: 700;
  text-decoration: none;
  line-height: 1.5;
}
.action-primary {
  background: var(--burgundy);
  color: #fff;
}
.action-primary:hover {
  background: #601c26;
}
.action-secondary {
  background: transparent;
  color: var(--burgundy);
}
.action-secondary:hover {
  background: #f5e9e8;
}
.content-panel {
  padding: clamp(1.25rem, 3vw, 2rem);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: .5rem;
}
@media screen and (max-width: 700px) {
  .page-shell {
    padding-top: 2rem;
    padding-bottom: 3rem;
  }
}
.skip-link {
  position: absolute;
  top: 1rem;
  transform: translateY(calc(-100% - 2rem));
  left: 1rem;
  z-index: 1000;
  padding: .75rem 1rem;
  background: #fff;
  color: #7f2532;
  font-weight: 700;
}
.skip-link:focus {
  transform: none;
}
#app {
  background-color: #faf7f3;
  color: #2c3e50;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  line-height: 1.65;

}
@media (prefers-reduced-motion: reduce) {
  .skip-link, .site-header, .site-header *,
  main, main *, .site-footer, .site-footer * {
    animation: none !important;
    transition: none !important;
    scroll-behavior: auto !important;
  }
}
</style>
